"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import Image from "next/image"
import {
  PEPTALK_CDN,
  PEPTALK_BANNER,
  PEPTALK_INTRO,
  PEPTALK_EPISODES,
} from "@lib/data/product-research-content"

const HERO_IMAGE = `${PEPTALK_CDN}/peptalk-hero.webp`

// Episodes whose products are not in the live catalog — keep them off the public hub.
const EXCLUDE = new Set([
  "metabolix",
  "neuro-enhance",
  "gut-repair",
  "hgh-frag",
  "pt-141",
  "kisspeptin",
  "cjc-1295",
])

type Track = {
  id: string
  eyebrow: string
  title: string
  blurb: string
  audio: string
}

const INTRO_TRACK: Track = {
  id: "intro",
  eyebrow: PEPTALK_INTRO.eyebrow,
  title: PEPTALK_INTRO.title,
  blurb: PEPTALK_INTRO.blurb,
  audio: PEPTALK_INTRO.audio,
}

const EPISODES: Track[] = Object.entries(PEPTALK_EPISODES)
  .filter(([handle]) => !EXCLUDE.has(handle))
  .map(([handle, ep]) => ({
    id: handle,
    eyebrow: ep.compound,
    title: ep.title,
    blurb: ep.blurb,
    audio: `${PEPTALK_CDN}/Peptide-Dialogues-${handle}.mp3?v=5`,
  }))

const fmt = (s?: number) =>
  !s || isNaN(s)
    ? "--:--"
    : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`

export default function PodcastPage() {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [playing, setPlaying] = useState(false)
  const [current, setCurrent] = useState(0)
  const [duration, setDuration] = useState(0)
  const [durations, setDurations] = useState<Record<string, number>>({})

  const allTracks = useMemo(() => [INTRO_TRACK, ...EPISODES], [])

  // Lazy-load each episode's duration for the list display.
  useEffect(() => {
    let cancelled = false
    allTracks.forEach((t) => {
      const a = new Audio()
      a.preload = "metadata"
      a.src = t.audio
      a.onloadedmetadata = () => {
        if (!cancelled) setDurations((d) => ({ ...d, [t.id]: a.duration }))
      }
    })
    return () => { cancelled = true }
  }, [allTracks])

  const playTrack = (track: Track) => {
    const audio = audioRef.current
    if (!audio) return
    if (activeId === track.id) {
      if (audio.paused) { void audio.play(); setPlaying(true) }
      else { audio.pause(); setPlaying(false) }
      return
    }
    audio.src = track.audio
    void audio.play()
    setActiveId(track.id)
    setCurrent(0)
    setPlaying(true)
  }

  const onSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current
    if (!audio || !audio.duration) return
    const t = (Number(e.target.value) / 100) * audio.duration
    audio.currentTime = t
    setCurrent(t)
  }

  const skip = (delta: number) => {
    const audio = audioRef.current
    if (!audio || !audio.src) return
    audio.currentTime = Math.min(Math.max(0, audio.currentTime + delta), audio.duration || 0)
  }

  const renderCard = (track: Track) => {
    const isActive = activeId === track.id
    const isPlaying = isActive && playing
    const pct = isActive && duration ? (current / duration) * 100 : 0

    return (
      <div
        key={track.id}
        className={`overflow-hidden rounded-2xl border bg-white shadow-sm transition-all ${
          isActive
            ? "border-[#008080] ring-1 ring-[#008080]/30"
            : "border-slate-200 hover:-translate-y-0.5 hover:border-[#008080]/40 hover:shadow-md"
        }`}
      >
        {/* Banner with play button overlay — click anywhere to play/pause */}
        <button
          onClick={() => playTrack(track)}
          className="group relative block aspect-[1700/562] w-full"
          aria-label={isPlaying ? `Pause ${track.title}` : `Play ${track.title}`}
        >
          <Image
            src={PEPTALK_BANNER}
            alt=""
            fill
            sizes="(min-width: 768px) 400px, 100vw"
            className="object-cover"
          />
          {/* Subtle scrim that lifts on hover so the wordmark stays readable */}
          <span className="absolute inset-0 bg-[#04201f]/5 transition-colors group-hover:bg-[#04201f]/20" />

          {/* Play / pause button — top-right */}
          <span
            className={`absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full border-2 border-white/90 transition-colors ${
              isActive ? "bg-[#008080] text-white" : "bg-white/15 text-white backdrop-blur-sm group-hover:bg-[#008080]"
            }`}
          >
            {isPlaying ? (
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
                <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="ml-0.5 h-4 w-4 fill-current">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </span>
        </button>

        {/* Episode details — clean strip below the banner */}
        <button
          onClick={() => playTrack(track)}
          className="block w-full px-4 py-3.5 text-left sm:px-5"
        >
          <span className="block text-[10px] font-bold uppercase tracking-[0.12em] text-[#008080]">
            {track.eyebrow}
          </span>
          <span className="mt-0.5 block text-[16px] font-bold leading-snug text-slate-900">
            {track.title}
          </span>
          <span className="mt-1.5 inline-flex items-center gap-1.5 text-xs tabular-nums text-slate-400">
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-none stroke-current" strokeWidth={2}>
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" strokeLinecap="round" />
            </svg>
            <span className="capitalize">{track.blurb}</span>
            <span className="text-slate-300">·</span>
            {isActive ? `${fmt(current)} / ${fmt(duration)}` : fmt(durations[track.id])}
          </span>
        </button>

        {/* Inline transport — only shown for the active track */}
        {isActive && (
          <div className="flex items-center gap-3 border-t border-slate-100 bg-[#008080]/[0.04] px-4 py-3 sm:px-5">
            <button
              onClick={() => skip(-15)}
              className="rounded-md border border-[#cfe3e3] bg-white px-2.5 py-1.5 text-xs font-bold text-[#008080] hover:bg-[#f0f9f9]"
            >
              « 15s
            </button>
            <input
              type="range"
              min={0}
              max={100}
              step={0.1}
              value={pct}
              onChange={onSeek}
              className="h-1 flex-1 cursor-pointer accent-[#008080]"
              aria-label="Seek"
            />
            <span className="w-10 text-right text-[11px] tabular-nums text-slate-400">
              {fmt(duration)}
            </span>
            <button
              onClick={() => skip(15)}
              className="rounded-md border border-[#cfe3e3] bg-white px-2.5 py-1.5 text-xs font-bold text-[#008080] hover:bg-[#f0f9f9]"
            >
              15s »
            </button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="bg-slate-50">
      {/* Hero */}
      <section className="relative w-full overflow-hidden bg-[#04201f]">
        <div className="absolute inset-0">
          <Image
            src={HERO_IMAGE}
            alt=""
            fill
            priority
            sizes="100vw"
            className="object-cover object-right sm:object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#04201f] via-[#04201f]/55 to-transparent" />
        </div>
        <div className="relative mx-auto max-w-6xl px-6 py-20 md:py-28">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[#5fe3d0]">
            The FastPeps Podcast
          </p>
          <h1 className="text-5xl font-extrabold tracking-tight text-white md:text-6xl">
            PepTalk
          </h1>
          <p className="mt-2 text-lg font-medium text-[#bfe9e3] md:text-xl">
            Research You Can Hear
          </p>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-slate-300 md:text-base">
            Short, plain-language episodes on the science behind the compounds researchers
            study — how the molecules work, what the literature shows, and where the evidence
            stands. Educational and for research-use only.
          </p>
        </div>
      </section>

      {/* Episode list */}
      <section className="mx-auto max-w-5xl px-4 py-12">
        <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.12em] text-[#008080]">
          Start here
        </p>
        <div className="sm:max-w-md">{renderCard(INTRO_TRACK)}</div>

        <p className="mb-3 mt-10 text-[11px] font-bold uppercase tracking-[0.12em] text-[#008080]">
          Episodes
        </p>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">{EPISODES.map(renderCard)}</div>

        <p className="mt-8 rounded-xl border border-slate-200 bg-white px-5 py-4 text-[11px] leading-relaxed text-slate-400">
          For research and educational purposes only. PepTalk discusses what researchers study
          in laboratory settings — it is not medical advice, and nothing here is a
          recommendation to use any compound. Products are sold for laboratory research use
          only.
        </p>
      </section>

      <audio
        ref={audioRef}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onEnded={() => { setPlaying(false); setActiveId(null); setCurrent(0) }}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
    </div>
  )
}
